import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Card from '../../components/Card.jsx';
import Chip from '../../components/Chip.jsx';
import Button from '../../components/Button.jsx';
import { strategyApi, backtestApi } from '../../lib/apiClient.js';

// Strategy Marketplace — Schedule B strategy copier. Publish a strategy from
// your track-record ledger; discover others' strategies and Copy & Run them
// (one-click re-run of the exact spec in Backtest Studio). No execution
// happens here — copying re-runs the research, per Clause 2.4.
export default function Strategies() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [list, setList] = useState(null);
  const [error, setError] = useState('');
  const [copying, setCopying] = useState(null);

  const [ledger, setLedger] = useState(null);
  const [pubTitle, setPubTitle] = useState('');
  const [pubTags, setPubTags] = useState('');
  const [publishing, setPublishing] = useState(null);
  const [publishError, setPublishError] = useState('');

  const publishedFlag = params.get('published') === '1';

  useEffect(() => {
    let alive = true;
    strategyApi
      .list()
      .then((data) => alive && setList(Array.isArray(data) ? data : []))
      .catch(() => alive && setList([]));
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!publishedFlag) return;
    backtestApi
      .ledger()
      .then((data) => setLedger(Array.isArray(data) ? data : []))
      .catch(() => setLedger([]));
  }, [publishedFlag]);

  async function copyStrategy(id) {
    setCopying(id);
    setError('');
    try {
      const out = await strategyApi.copy(id);
      navigate('/app/backtest', { state: { spec: out.spec, strategyTitle: out.title } });
    } catch (err) {
      setError(err.message || 'Copy failed — please sign in.');
      setCopying(null);
    }
  }

  async function publish(runId) {
    if (!pubTitle.trim()) {
      setPublishError('Give the strategy a title first.');
      return;
    }
    setPublishing(runId);
    setPublishError('');
    try {
      await strategyApi.publish({
        ledgerRunId: runId,
        title: pubTitle.trim(),
        tags: pubTags.split(',').map((t) => t.trim()).filter(Boolean),
      });
      setPubTitle('');
      setPubTags('');
      setPublishing(null);
      const data = await strategyApi.list();
      setList(Array.isArray(data) ? data : []);
    } catch (err) {
      setPublishError(err.message || 'Publish failed.');
      setPublishing(null);
    }
  }

  return (
    <>
      <Card>
        <h1 className="heading" style={{ fontSize: 22 }}>
          Strategy Marketplace
        </h1>
        <p className="aq-module-desc">
          Discover strategies published from verified backtest runs and copy them into Backtest Studio for a
          one-click re-run. Copying re-runs the exact strategy spec — no execution, technology layer only.
        </p>
      </Card>

      {publishedFlag && (
        <Card className="aq-qual">
          <h3 className="heading" style={{ fontSize: 16, marginBottom: 8 }}>
            Publish a strategy from your ledger
          </h3>
          <p className="aq-module-desc" style={{ marginBottom: 10 }}>
            Your ledger runs are below. Add a title and tags, then publish — the strategy (with its track record)
            becomes publicly discoverable.
          </p>
          <div className="aq-add-pos" style={{ marginBottom: 8 }}>
            <input
              type="text"
              placeholder="Strategy title (e.g. NGX 20-day breakout)"
              value={pubTitle}
              onChange={(e) => setPubTitle(e.target.value)}
              style={{ flex: 1, minWidth: 200 }}
            />
            <input
              type="text"
              placeholder="Tags, comma-separated"
              value={pubTags}
              onChange={(e) => setPubTags(e.target.value)}
              style={{ flex: 1, minWidth: 160 }}
            />
          </div>
          {publishError && <p className="aq-auth-alert aq-auth-alert--error">{publishError}</p>}
          {ledger === null && <p className="aq-module-desc">Loading your ledger…</p>}
          {ledger && ledger.length === 0 && (
            <p className="aq-module-desc">No ledger runs to publish yet — run a backtest in Studio first.</p>
          )}
          {ledger && ledger.length > 0 && (
            <div className="aq-history-list">
              {ledger
                .filter((r) => r.strategySpec)
                .map((r) => (
                  <div key={r.runId} className="aq-history-row" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'default' }}>
                    <span className="aq-history-mode">{r.mode || r.runId?.slice(0, 8)}</span>
                    <span className="aq-history-date">{new Date(r.submittedAt).toLocaleString()}</span>
                    <span className="aq-history-meta">
                      {r.metrics?.overall
                        ? `${(r.metrics.overall.totalReturn ?? 0).toFixed(2)}% · Sharpe ${(r.metrics.overall.sharpe ?? 0).toFixed(2)}`
                        : '—'}
                      {r.symbols?.length ? ` · ${r.symbols.slice(0, 3).join(', ')}` : ''}
                    </span>
                    <span style={{ flex: 1 }} />
                    <Button size="sm" variant="secondary" disabled={publishing === r.runId} onClick={() => publish(r.runId)}>
                      {publishing === r.runId ? 'Publishing…' : 'Publish'}
                    </Button>
                  </div>
                ))}
            </div>
          )}
        </Card>
      )}

      <section>
        <h2 className="aq-section-title heading">Discover strategies</h2>
        {error && <p className="aq-auth-alert aq-auth-alert--error">{error}</p>}
        {list === null && <Card>Loading…</Card>}
        {list && list.length === 0 && !error && (
          <Card>
            <div className="aq-empty-state">
              No strategies published yet. Run a backtest in Studio and publish it from your track record.
            </div>
          </Card>
        )}
        <div className="aq-grid aq-grid--2">
          {(list || []).map((s) => {
            const m = s.metrics?.overall;
            return (
              <Card key={s.id} className="aq-rsr">
                <div className="aq-rsr-head">
                  <h3 className="heading" style={{ fontSize: 16 }}>
                    {s.title}
                  </h3>
                  <Chip tone="gain">Track-recorded</Chip>
                </div>
                {s.description && <p className="aq-module-desc">{s.description}</p>}
                <div className="aq-chip-row">
                  {(s.tags || []).map((t) => (
                    <Chip key={t}>{t}</Chip>
                  ))}
                </div>
                <div className="aq-stat-row">
                  <span className="aq-stat">
                    Return <strong className="num">{(m?.totalReturn ?? 0).toFixed(2)}%</strong>
                  </span>
                  <span className="aq-stat">
                    Sharpe <strong className="num">{(m?.sharpe ?? 0).toFixed(2)}</strong>
                  </span>
                  <span className="aq-stat">
                    Max DD <strong className="num">{(m?.maxDrawdown ?? 0).toFixed(2)}%</strong>
                  </span>
                  <span className="aq-stat">
                    Copies <strong className="num">{s.copies ?? 0}</strong>
                  </span>
                </div>
                <p className="aq-news-meta">
                  {s.symbols?.length ? `Symbols: ${s.symbols.slice(0, 4).join(', ')}${s.symbols.length > 4 ? '…' : ''}` : ''}
                  {s.period ? ` · ${s.period.from} → ${s.period.to}` : ''}
                </p>
                <Button size="sm" disabled={copying === s.id} onClick={() => copyStrategy(s.id)}>
                  {copying === s.id ? 'Copying…' : 'Copy & Run →'}
                </Button>
              </Card>
            );
          })}
        </div>
      </section>
    </>
  );
}
